import type { PreconditionReason } from '@crowdex/agent-contracts';
import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import {
  Cloud,
  FolderDown,
  FolderOpen,
  Laptop,
  LoaderCircle,
  RotateCw,
  WifiOff,
} from 'lucide-react';
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  type CloudProjectMissingActions,
  CloudProjectMissingNotice,
} from '@/components/desktop-shell/cloud-project-missing-notice';
import { ErrorBanner, NoticeBanner } from '@/components/desktop-shell/error-banner';
import { useFloatingConversation } from '@/components/desktop-shell/floating-conversation';
import {
  type ConversationFailure,
  preconditionTitle,
} from '@/lib/conversations/precondition-failure';
import { explainTurnFailure, TRY_AGAIN_MESSAGE } from '@/lib/conversations/turn-failure';
import { useDesktopPreviewState } from '@/lib/desktop-preview-state';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { NO_CLOUD_RUNTIME_MESSAGE } from '@/lib/desktop-shell/use-cloud-runtime';
import { isLocalMachine } from '@/lib/local-runtime';
import { useOnline } from '@/lib/online-status';
import { pluginsPath } from '@/lib/plugins/plugin-sections';
import { MODELS_PATH } from '@/lib/settings-page/sections';
import { navigateApp } from '@/vite/navigation-store';

const SECONDARY_ACTION =
  'inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 font-medium text-xs hover:bg-muted disabled:opacity-50';
const PRIMARY_ACTION =
  'inline-flex h-7 items-center gap-1.5 rounded-md bg-foreground px-2.5 font-medium text-background text-xs hover:bg-foreground/85 disabled:opacity-50';

/** The way to fix what is missing, as the banner's buttons. */
function PreconditionActions({
  reason,
  onSignInAgent,
  onSelectWorkspace,
}: {
  reason: PreconditionReason;
  onSignInAgent?: (() => void) | undefined;
  onSelectWorkspace?: (() => void) | undefined;
}): ReactNode {
  const go = (href: string, label: string, primary = true) => (
    <button
      className={primary ? PRIMARY_ACTION : SECONDARY_ACTION}
      onClick={() => navigateApp(href)}
      type="button"
    >
      {label}
    </button>
  );
  switch (reason) {
    case 'no_agent':
      return (
        <>
          {go(pluginsPath(), 'Browse plugins', false)}
          {go('/settings?section=agents', 'Choose an agent')}
        </>
      );
    case 'agent_signed_out':
      return (
        <>
          {go('/settings?section=agents', 'Agent settings', onSignInAgent === undefined)}
          {onSignInAgent !== undefined && (
            <button className={PRIMARY_ACTION} onClick={onSignInAgent} type="button">
              Sign in
            </button>
          )}
        </>
      );
    case 'no_model_provider':
      // Models come through Crowpus: Settings › Models says what it offers.
      return go(MODELS_PATH, 'Models');
    case 'model_provider_missing':
      // The default model (Settings › Models), or the one chosen for an agent (Agents).
      return (
        <>
          {go('/settings?section=agents', 'Agent models', false)}
          {go(MODELS_PATH, 'Default model')}
        </>
      );
    case 'cloud_unlinked':
      return go('/settings?section=account', 'Sign in to Crowdex Cloud');
    case 'cloud_project_missing':
      // Its ways on (this computer's folder, another, the forge's copy) are the cloud start's own.
      return null;
    case 'folder_missing':
    case 'not_a_repository':
      return onSelectWorkspace === undefined ? null : (
        <button className={PRIMARY_ACTION} onClick={onSelectWorkspace} type="button">
          <FolderOpen className="size-3.5" /> Open another folder
        </button>
      );
  }
}

/** A failure: with what is missing and the way to fix it when known, else its message. */
export function FailureBanner({
  failure,
  agentName,
  onSignInAgent,
  onSelectWorkspace,
  layout,
}: {
  failure: ConversationFailure;
  agentName: string | null;
  onSignInAgent?: (() => void) | undefined;
  onSelectWorkspace?: (() => void) | undefined;
  layout?: { compact?: boolean; className?: string };
}) {
  if (failure.reason === null) return <ErrorBanner {...layout}>{failure.message}</ErrorBanner>;
  // The folder notice has its own fixes (locate, clone, cloud) where the conversation knows them.
  return (
    <NoticeBanner
      {...layout}
      actions={
        <PreconditionActions
          onSelectWorkspace={onSelectWorkspace}
          onSignInAgent={onSignInAgent}
          reason={failure.reason}
        />
      }
      title={preconditionTitle(failure.reason, agentName)}
      tone="warning"
    >
      {failure.message}
    </NoticeBanner>
  );
}

/**
 * Offline, discreet above the transcript (D53): a conversation running in the cloud keeps going
 * there; anywhere else (a new conversation, this computer) what is sent waits for the network.
 */
function OfflineBanner({ cloud }: { cloud: boolean }) {
  return (
    <div
      className="type-meta mx-2 mt-2 flex shrink-0 items-center gap-2 rounded-lg bg-control/70 px-4 py-1.5 text-foreground/85"
      data-offline-banner
      role="status"
    >
      {/* Neutral: amber is for what waits on you. */}
      <WifiOff aria-hidden className="size-3.5 text-muted-foreground" />
      {cloud
        ? 'Offline. The agent keeps working in the cloud.'
        : "Offline. Messages will send when you're back online."}
    </div>
  );
}

/**
 * The first message of a cloud conversation found no cloud sandbox to run in (H6): it stays in
 * the composer, and the banner says why with the two ways on, running it here or setting the
 * cloud up.
 */
function NoCloudSandboxNotice({
  layout,
  onRunOnThisComputer,
  setupPath,
}: {
  layout?: { compact?: boolean; className?: string };
  /** Runs it on this computer instead (Runs in › This computer), where it can. */
  onRunOnThisComputer?: (() => void) | undefined;
  /** Where the cloud is set up. */
  setupPath: string;
}) {
  // A message sent offline waits in the queue rather than in the composer.
  const queued = useDesktopShellStore(
    (state) => (state.queuedFollowUps[state.chatId]?.length ?? 0) > 0,
  );
  return (
    <NoticeBanner
      {...layout}
      actions={
        <>
          {onRunOnThisComputer !== undefined && (
            <Button
              className="h-8 rounded-control px-3.5"
              onClick={onRunOnThisComputer}
              size="sm"
              variant="outline"
            >
              <Laptop aria-hidden className="size-3.5" /> Run on this computer
            </Button>
          )}
          <Button
            className="h-8 rounded-control px-3.5"
            onClick={() => navigateApp(setupPath)}
            size="sm"
            variant="outline"
          >
            <Cloud aria-hidden className="size-3.5" /> Set up the cloud
          </Button>
        </>
      }
      title="No cloud sandbox is set up yet."
      tone="warning"
    >
      {queued
        ? "Your message wasn't sent; it waits in the queue."
        : "Your message wasn't sent; it is still in the composer."}
    </NoticeBanner>
  );
}

/**
 * The message just sent did not leave (its cloud agent could not start): it is back in the
 * composer, editable, and Retry sends what the composer holds again.
 */
function MessageNotSentNotice({
  reason,
  layout,
}: {
  reason: string;
  layout?: { compact?: boolean; className?: string };
}) {
  return (
    <ErrorBanner
      {...layout}
      actions={
        <Button
          aria-label="Retry sending"
          className="h-8 rounded-control px-3.5"
          onClick={(event) =>
            event.currentTarget
              .closest('[data-conversation-pane]')
              ?.querySelector<HTMLFormElement>('form.desktop-composer')
              ?.requestSubmit()
          }
          size="sm"
          variant="outline"
        >
          <RotateCw aria-hidden className="size-3.5" /> Retry
        </Button>
      }
      title="Your message wasn't sent."
    >
      {`${reason.replace(/\.?$/, '.')} It is still in the composer.`}
    </ErrorBanner>
  );
}

/** Retry, for conversations that could not be loaded. */
function RetrySyncButton({ onRetry }: { onRetry: () => void }) {
  return (
    <Button className="h-8 rounded-control px-3.5" onClick={onRetry} size="sm" variant="outline">
      <RotateCw aria-hidden className="size-3.5" /> Retry
    </Button>
  );
}

/**
 * Shows its children at the end of its pane's transcript (`[data-turn-stopped-slot]`, where the
 * turn stopped, or `[data-queued-slot]` after it, for what waits to be sent); in place when the
 * pane has no transcript yet.
 */
export function AtTranscriptEnd({
  children,
  slot: name = 'turn-stopped',
}: {
  children: ReactNode;
  slot?: 'turn-stopped' | 'queued';
}) {
  const marker = useRef<HTMLSpanElement>(null);
  const [slot, setSlot] = useState<Element | null>(null);
  useLayoutEffect(() => {
    const found =
      marker.current?.closest('[data-conversation-pane]')?.querySelector(`[data-${name}-slot]`) ??
      null;
    if (found !== slot) setSlot(found);
  });
  return (
    <>
      <span hidden ref={marker} />
      {slot === null ? children : createPortal(children, slot)}
    </>
  );
}

/**
 * A turn that stopped on an error (design D53): what happened, that nothing was lost, Try again
 * (the agent picks up where it stopped) and Details (the error itself).
 */
function TurnStoppedNotice({
  message,
  onRetry,
  compact = false,
}: {
  message: string;
  onRetry?: (() => void) | undefined;
  compact?: boolean;
}) {
  const [details, setDetails] = useState(false);
  return (
    <div
      className={cn(
        'mx-auto w-full shrink-0',
        compact ? 'px-3 pt-2' : 'max-w-conversation px-4 pt-3',
        // In the transcript, it takes the column's own width.
        '[[data-turn-stopped-slot]_&]:max-w-none [[data-turn-stopped-slot]_&]:p-0',
      )}
    >
      <section
        className="rounded-card border border-destructive/40 bg-destructive/10 px-4 py-3"
        data-turn-stopped
        role="alert"
      >
        <p className="font-semibold text-foreground text-sm">The turn stopped</p>
        <p className="type-meta mt-0.5 text-foreground/85">{explainTurnFailure(message)}</p>
        {details && (
          <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap rounded-xl bg-code-surface px-3 py-2 font-mono text-muted-foreground text-xs">
            {message}
          </pre>
        )}
        <div className="mt-2.5 flex gap-2">
          {onRetry !== undefined && (
            <Button className="h-8 rounded-control px-3.5" onClick={onRetry} size="sm">
              Try again
            </Button>
          )}
          <Button
            aria-expanded={details}
            className="h-8 rounded-control px-3.5"
            onClick={() => setDetails((open) => !open)}
            size="sm"
            variant="outline"
          >
            Details
          </Button>
        </div>
      </section>
    </div>
  );
}

/**
 * Stack of inline error banners shown under the conversation header. The
 * shell-owned failures (workspace sync, cloud provisioning, agent handoff) are
 * read straight from the store; the remaining messages originate outside the
 * store (the live agent connection, the chat transport, message sync, and the
 * cloud-handoff dialog) and arrive as props.
 */
export function ConversationErrorBanners({
  connectionError,
  chatError,
  agentName = null,
  onSignInAgent,
  onSelectWorkspace,
  syncError,
  cloudHandoffError,
  cloudHandoffRemoteNotice,
  workspaceRecovery,
  workspaceRecoveryWorking = false,
  onLocateWorkspace,
  onCloneWorkspace,
  onContinueInCloud,
  onRetryTurn,
  started = true,
  onRetrySync,
  onRunOnThisComputer,
  cloudProjectMissing = null,
}: {
  connectionError: ConversationFailure | null;
  chatError: ConversationFailure | null;
  /** The conversation's agent, named in "not signed in" notices. */
  agentName?: string | null;
  /** Opens a terminal signing the agent in, when it says how. */
  onSignInAgent?: (() => void) | undefined;
  /** Opens another folder, for a conversation whose folder is gone. */
  onSelectWorkspace?: (() => void) | undefined;
  syncError: string | null;
  cloudHandoffError: string | null;
  cloudHandoffRemoteNotice: string | null;
  workspaceRecovery?: { name: string; cloneable: boolean; cloudAvailable: boolean } | null;
  workspaceRecoveryWorking?: boolean;
  /** Continues here a conversation that runs in a folder of another machine. */
  onLocateWorkspace?: () => void;
  onCloneWorkspace?: () => void;
  onContinueInCloud?: () => void;
  /** Try again on a stopped turn (D53): sends the message that has the agent pick up. */
  onRetryTurn?: (message: string) => void;
  /** The conversation has messages; a new one (its start screen) has no turn to stop. */
  started?: boolean;
  /** Loads the conversations again after their sync failed. */
  onRetrySync?: (() => void) | undefined;
  /** Runs the conversation on this computer (Runs in), where this computer can. */
  onRunOnThisComputer?: (() => void) | undefined;
  /** The ways on when Crowdex Cloud keeps no copy of the project (`cloud_project_missing`). */
  cloudProjectMissing?: CloudProjectMissingActions | null;
}) {
  const online = useOnline();
  const previewTurnFailure = useDesktopPreviewState((state) => state.turnFailure);
  const previewInCloud = useDesktopPreviewState((state) => state.inCloud);
  const cloudConversation =
    useDesktopShellStore((state) => state.executionMode === 'cloud') || previewInCloud;
  const workspaceSyncError = useDesktopShellStore((state) => state.workspaceSyncError);
  const workspaceProvisionError = useDesktopShellStore((state) => state.workspaceProvisionError);
  const cloudProvisionError = useDesktopShellStore((state) => state.cloudProvisionError);
  const messageNotSent = useDesktopShellStore((state) => state.messageNotSent);
  const setCloudProvisionError = useDesktopShellStore((state) => state.setCloudProvisionError);
  const agentHandoffError = useDesktopShellStore((state) => state.agentHandoffError);
  const localHandoffOpen = useDesktopShellStore((state) => state.localHandoffOpen);
  // Over a maximized side panel the conversation shrinks to a floating composer:
  // notices shrink with it, sized to their text and stacked just above it.
  const floating = useFloatingConversation();
  const layout = floating ? { compact: true, className: 'px-0 pt-0' } : {};

  // No cloud sandbox to start the conversation in (H6): said once, with the ways on.
  const noCloudSandbox = cloudProvisionError === NO_CLOUD_RUNTIME_MESSAGE;
  // A message that did not leave is always said, sync failure or not: never a silent composer.
  const notSent = messageNotSent !== null && !noCloudSandbox;
  // A new conversation whose sync failed has one thing to say (M1): its other failures (no turn
  // to retry, a sandbox that could not be asked for) all come from it.
  const syncFailedBeforeStart = !started && workspaceSyncError !== null;
  const turnFailure =
    (chatError !== null && chatError.reason === null) || previewTurnFailure !== null;
  const retrySync =
    onRetrySync === undefined ? undefined : <RetrySyncButton onRetry={onRetrySync} />;

  const banners = (
    <>
      {!online && !floating && <OfflineBanner cloud={cloudConversation && started} />}
      {noCloudSandbox && (
        <NoCloudSandboxNotice
          layout={layout}
          onRunOnThisComputer={
            onRunOnThisComputer === undefined || started
              ? undefined
              : () => {
                  setCloudProvisionError(null);
                  onRunOnThisComputer();
                }
          }
          setupPath={isLocalMachine() ? '/settings?section=account' : pluginsPath()}
        />
      )}
      {notSent && <MessageNotSentNotice layout={layout} reason={messageNotSent ?? ''} />}
      {connectionError !== null && online && (
        <FailureBanner
          agentName={agentName}
          failure={connectionError}
          layout={layout}
          onSignInAgent={onSignInAgent}
        />
      )}
      {/* A turn that failed without a known cause: what happened, and Try again. */}
      {turnFailure && started && (
        <AtTranscriptEnd>
          <TurnStoppedNotice
            compact={layout.compact === true}
            message={chatError?.message ?? previewTurnFailure ?? ''}
            onRetry={onRetryTurn && (() => onRetryTurn(TRY_AGAIN_MESSAGE))}
          />
        </AtTranscriptEnd>
      )}
      {/* A folder that is gone is told, with its fixes, by the recovery notice below. */}
      {chatError !== null &&
        chatError.reason !== null &&
        !(chatError.reason === 'folder_missing' && workspaceRecovery != null) &&
        (chatError.reason !== connectionError?.reason || chatError.reason === null) && (
          <FailureBanner
            agentName={agentName}
            failure={chatError}
            layout={layout}
            onSelectWorkspace={onSelectWorkspace}
            onSignInAgent={onSignInAgent}
          />
        )}
      {/* Before the first message there is no turn: the failure is said as it is. */}
      {turnFailure && !started && !syncFailedBeforeStart && (
        <ErrorBanner {...layout}>{chatError?.message ?? previewTurnFailure}</ErrorBanner>
      )}
      {syncError !== null && !syncFailedBeforeStart && (
        <ErrorBanner {...layout} title="Conversation could not be saved.">
          {syncError}
        </ErrorBanner>
      )}
      {workspaceSyncError !== null && (
        <ErrorBanner {...layout} actions={retrySync} title="Conversations couldn't sync.">
          {/* Before the first message, the title alone says it; the details are for a turn. */}
          {started ? workspaceSyncError : undefined}
        </ErrorBanner>
      )}
      {workspaceRecovery != null && (
        <NoticeBanner
          {...layout}
          actions={
            <>
              {onLocateWorkspace !== undefined && (
                <button
                  className={SECONDARY_ACTION}
                  disabled={workspaceRecoveryWorking}
                  onClick={onLocateWorkspace}
                  type="button"
                >
                  <FolderOpen className="size-3.5" /> Locate
                </button>
              )}
              {workspaceRecovery.cloneable && onCloneWorkspace !== undefined && (
                <button
                  className={SECONDARY_ACTION}
                  disabled={workspaceRecoveryWorking}
                  onClick={onCloneWorkspace}
                  type="button"
                >
                  <FolderDown className="size-3.5" /> Clone
                </button>
              )}
              {workspaceRecovery.cloudAvailable && onContinueInCloud !== undefined && (
                <button
                  className={PRIMARY_ACTION}
                  disabled={workspaceRecoveryWorking}
                  onClick={onContinueInCloud}
                  type="button"
                >
                  {workspaceRecoveryWorking ? (
                    <LoaderCircle className="size-3.5 animate-spin" />
                  ) : (
                    <Cloud className="size-3.5" />
                  )}
                  Continue in Cloud
                </button>
              )}
            </>
          }
          title={`${workspaceRecovery.name} is not available on this device.`}
          tone="warning"
        >
          {workspaceProvisionError ?? undefined}
        </NoticeBanner>
      )}
      {workspaceProvisionError !== null && workspaceRecovery == null && (
        <ErrorBanner {...layout} title="Workspace could not be prepared.">
          {workspaceProvisionError}
        </ErrorBanner>
      )}
      {cloudProjectMissing !== null && !syncFailedBeforeStart && (
        <CloudProjectMissingNotice actions={cloudProjectMissing} layout={layout} />
      )}
      {cloudProvisionError !== null &&
        cloudProjectMissing === null &&
        !noCloudSandbox &&
        !notSent &&
        !syncFailedBeforeStart && (
          <ErrorBanner {...layout} title="Cloud agent could not start.">
            {cloudProvisionError}
          </ErrorBanner>
        )}
      {agentHandoffError !== null && !localHandoffOpen && (
        <ErrorBanner {...layout} title="Agent could not be changed.">
          {agentHandoffError}
        </ErrorBanner>
      )}
      {cloudHandoffError !== null && <ErrorBanner {...layout}>{cloudHandoffError}</ErrorBanner>}
      {cloudHandoffRemoteNotice !== null && (
        <NoticeBanner {...layout} tone="info">
          {cloudHandoffRemoteNotice}
        </NoticeBanner>
      )}
    </>
  );

  return floating ? (
    <div className="pointer-events-auto mx-auto mb-2 flex w-fit max-w-[min(32rem,calc(100vw-2rem))] flex-col gap-1.5 empty:hidden">
      {banners}
    </div>
  ) : (
    banners
  );
}
